/** `flow.grade` — a running grade from `graded` signals: once a progress
 * scalar reaches its count, a grade under the minimum loses. It decides the
 * match in the flow stage, ahead of `flow.objective` in spec order. (A shift
 * clock is `rule.resource` with `onEmpty: 'lose'`.) */
import { id, int, num, obj } from '../schemaBuilders.js';
import { defineModule, num as read } from '../module.js';

export type GradeConfig = {
  /** Minimum earned/possible ratio to keep the match alive at the end. */
  minRatio: number;
  /** The grade is final once this scalar reaches `untilCount`. */
  untilScalar: string;
  untilCount: number;
};

export const grade = defineModule<GradeConfig>({
  id: 'flow.grade', version: 1, attach: 'global', role: 'flow',
  summary: 'A running grade: graded signals add earned/possible points; once a progress scalar reaches its count, a grade under the minimum loses the match.',
  config: obj({ minRatio: num(0, 1), untilScalar: id(), untilCount: int(1, 1000) }),
  requires: [], events: ['below-target'], presentation: { 'below-target': { cue: 'lose', vfx: { effect: 'shake' } } },
  proofs: ['accumulates-graded', 'percent-hud', 'loses-below-minimum-at-the-end', 'passes-at-or-above-minimum'],
  animStates: () => [],
  hud: () => [{ id: 'rating', label: 'RATING', kind: 'meter' }],
  hudValues(ctx) {
    const possible = read(ctx.state, 'possible');
    return { rating: possible > 0 ? Math.round((100 * read(ctx.state, 'earned')) / possible) : 100 };
  },
  initGlobal(ctx) { Object.assign(ctx.state, { earned: 0, possible: 0 }); },
  systems: {
    flow(ctx) {
      const s = ctx.state, c = ctx.config;
      for (const g of ctx.signals('graded')) {
        if (typeof g.data.earned === 'number' && typeof g.data.possible === 'number') {
          s.earned = read(s, 'earned') + g.data.earned;
          s.possible = read(s, 'possible') + g.data.possible;
        }
      }
      if (ctx.world.phase !== 'active' || ctx.scalar(c.untilScalar, 0) < c.untilCount) return;
      const possible = read(s, 'possible');
      if (possible > 0 && read(s, 'earned') / possible >= c.minRatio) return;
      ctx.emit('below-target', { ratio: possible > 0 ? read(s, 'earned') / possible : 0 });
      ctx.end('lost', 'below-target');
    },
  },
});
